import {
  ANALYTICS_BATCH_MAX_EVENTS,
  type AnalyticsEventName,
  type AnalyticsProps,
} from '@ddp/shared';

import { apiFetch, getAccessToken } from '../../api/client.js';

/**
 * Client d'analytique d'usage (ADR-0026, incrément 2). Accumule les événements et les envoie **par
 * lots** — un envoi par clic saturerait l'API (NFR-8). L'analytique est **best-effort** : elle ne doit
 * jamais interrompre ni ralentir l'expérience, donc tout échec est silencieux et un lot perdu est
 * accepté.
 *
 * Ce qu'on n'envoie **jamais** : le contrat partagé (`props` typés, motif strict) rejette déjà e-mails,
 * URLs et texte libre ; côté appelant, on s'astreint en plus à ne passer que des **catégories et des
 * compteurs**, jamais un titre, une requête de recherche ou une question d'assistant.
 */

interface QueuedEvent {
  name: AnalyticsEventName;
  occurredAt: string;
  props?: AnalyticsProps;
}

const FLUSH_INTERVAL_MS = 10_000;
/** Vidage anticipé bien avant la borne serveur : on n'attend pas d'avoir un gros lot en mémoire. */
const FLUSH_AT = 20;

const queue: QueuedEvent[] = [];
let sessionId: string | null = null;
let started = false;

/** Identifiant de session **éphémère** et opaque : vit dans `sessionStorage` (le temps de l'onglet), jamais relié à l'identité. */
function ensureSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const existing = sessionStorage.getItem('ddp.analytics.sid');
    if (existing) {
      sessionId = existing;
      return existing;
    }
    const fresh = crypto.randomUUID();
    sessionStorage.setItem('ddp.analytics.sid', fresh);
    sessionId = fresh;
    return fresh;
  } catch {
    // sessionStorage indisponible (mode privé strict) : un id en mémoire suffit pour la session.
    sessionId = crypto.randomUUID();
    return sessionId;
  }
}

/** Enregistre un événement d'usage. Non bloquant : pousse en file, déclenche un vidage si le seuil est atteint. */
export function track(name: AnalyticsEventName, props?: AnalyticsProps): void {
  queue.push({ name, occurredAt: new Date().toISOString(), props });
  if (queue.length >= FLUSH_AT) {
    void flush();
  }
}

/**
 * Envoie les événements en file. Sans jeton d'accès (utilisateur non connecté), l'endpoint refuserait
 * le lot : on le jette plutôt que de le bufferiser indéfiniment. `keepalive` permet à l'envoi de
 * survivre à la fermeture de l'onglet.
 */
export async function flush(keepalive = false): Promise<void> {
  if (queue.length === 0) return;
  if (!getAccessToken()) {
    queue.length = 0;
    return;
  }
  const events = queue.splice(0, ANALYTICS_BATCH_MAX_EVENTS);
  try {
    await apiFetch('/analytics/events', {
      method: 'POST',
      body: { sessionId: ensureSessionId(), events },
      keepalive,
    });
  } catch {
    // Best-effort : on abandonne ce lot. L'analytique ne doit jamais remonter d'erreur à l'utilisateur.
  }
}

/**
 * Démarre le vidage périodique et le vidage « dernière chance » quand l'onglet passe en arrière-plan
 * (`visibilitychange` → `hidden` est le signal fiable avant fermeture ; `keepalive` fait le reste).
 * Idempotent.
 */
export function startAnalytics(): void {
  if (started) return;
  started = true;

  setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
}

/** Buckets de taille de fichier — évite d'émettre l'octet exact (faible cardinalité, ADR-0026 §2). */
export function sizeBucket(bytes: number): string {
  if (bytes < 100 * 1024 * 1024) return 'lt_100mb';
  if (bytes < 1024 * 1024 * 1024) return '100mb_1gb';
  if (bytes < 5 * 1024 * 1024 * 1024) return '1gb_5gb';
  return 'gt_5gb';
}
